import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, Wrench, Package, AlertTriangle, Coffee, CheckCircle, Camera, UserCircle } from "lucide-react";

const DowntimeBreakdown = ({ isOpen, onClose, downtimeEntries, totalDowntime }) => {
  // Get icon for reason code
  const getReasonIcon = (reasonCode) => {
    switch (reasonCode) {
      case "machine_down":
        return <Wrench className="w-5 h-5 text-red-600" />;
      case "missing_ingredients":
        return <Package className="w-5 h-5 text-orange-600" />;
      case "food_safety":
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      case "quality_check":
        return <CheckCircle className="w-5 h-5 text-blue-600" />;
      case "break":
        return <Coffee className="w-5 h-5 text-gray-600" />;
      case "changeover":
        return <Clock className="w-5 h-5 text-purple-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  // Get color for reason code
  const getReasonColor = (reasonCode) => {
    switch (reasonCode) {
      case "machine_down":
        return "bg-red-100 text-red-800 border-red-300";
      case "missing_ingredients":
        return "bg-orange-100 text-orange-800 border-orange-300";
      case "food_safety":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "quality_check":
        return "bg-blue-100 text-blue-800 border-blue-300";
      case "break":
        return "bg-gray-100 text-gray-800 border-gray-300";
      case "changeover":
        return "bg-purple-100 text-purple-800 border-purple-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  // Calculate percentage
  const getPercentage = (duration) => {
    return ((duration / totalDowntime) * 100).toFixed(1);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Clock className="w-6 h-6 text-amber-600" />
            Downtime Breakdown
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Summary */}
          <Card style={{ background: '#fff7ed', borderColor: '#fb923c' }}>
            <CardContent className="pt-4">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm text-gray-600">Total Downtime</div>
                  <div className="text-3xl font-bold text-amber-600">{totalDowntime} min</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">Number of Events</div>
                  <div className="text-3xl font-bold text-gray-700">{downtimeEntries?.length || 0}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Downtime Entries */}
          {downtimeEntries && downtimeEntries.length > 0 ? (
            <div className="space-y-3">
              {downtimeEntries.map((entry, index) => (
                <Card key={index} className={`border-2 ${getReasonColor(entry.reason_code)}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        {getReasonIcon(entry.reason_code)}
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="font-semibold text-gray-900 capitalize">
                              {entry.reason_code.replace(/_/g, ' ')}
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              {entry.reason_description}
                            </div>
                          </div>
                          <div className="text-right ml-4">
                            <div className="text-2xl font-bold text-amber-600">
                              {entry.duration_minutes} <span className="text-sm font-normal text-gray-500">min</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {getPercentage(entry.duration_minutes)}% of total
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 text-xs text-gray-500 mt-3 pt-3 border-t">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTime(entry.timestamp)}
                          </div>
                          <div className="flex items-center gap-1">
                            {entry.logged_by === "system" ? (
                              <>
                                <Camera className="w-3 h-3" />
                                AI Camera
                              </>
                            ) : (
                              <>
                                <UserCircle className="w-3 h-3" />
                                Manual Entry
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No downtime entries recorded</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DowntimeBreakdown;
